import React, { useEffect, useState } from 'react'
import { MEModal } from '@CommonComponents'
import { client } from '@Utils'
import { bookInfoEndpoints } from '@URLS'
import {
  PERSON_FIELDS,
  PERSON_FIELD_LABELS,
  addPerson,
  buildPayload,
  errorMessage,
  groupsFromResponse,
  movePerson,
  removePerson,
  updatePerson,
  validatePayload,
} from './contributors'

// Edits the title page's Authors / Contributors / Developers / Editors groups in one form.
// Saving rewrites bookinfo/authorgroup and reloads the page so the server re-renders the
// credits (the Mako markup is not duplicated here).
export const ContributorsModal = ({ coursenum, show, onClose, runMutation, isBusy }) => {
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!show) return undefined
    let cancelled = false
    setLoading(true)
    setError(null)
    client
      .get(bookInfoEndpoints.getContributors({ coursenum }))
      .then(resp => {
        if (!cancelled) setGroups(groupsFromResponse(resp.data))
      })
      .catch(err => {
        if (!cancelled)
          setError(errorMessage(err, 'The contributors could not be loaded.'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [show, coursenum])

  const handleSave = () => {
    const payload = buildPayload(groups)
    const problem = validatePayload(payload)
    if (problem) {
      setError(problem)
      return
    }
    setError(null)
    runMutation(async () => {
      try {
        await client.post(
          bookInfoEndpoints.saveContributors({ coursenum }),
          payload
        )
        window.location.reload()
      } catch (err) {
        setError(errorMessage(err, 'The contributors could not be saved.'))
      }
    })
  }

  return (
    <MEModal show={show} close={onClose} title="Edit contributors" size="lg">
      {error && <div className="alert alert-danger">{error}</div>}
      {loading ? (
        <div className="text-center my-4">
          <div className="icon icon_spinner2 icon_spin1 fs-xl text-secondary">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : (
        groups.map(group => (
          <div key={group.key} className="coverpage-contributor-group mb-4">
            <h4 className="mb-2">{group.role}</h4>
            {group.people.length === 0 && (
              <p className="text-muted mb-2">No one listed.</p>
            )}
            {group.people.map((person, index) => (
              <div
                key={`${group.key}-${index}`}
                className="coverpage-contributor-row d-flex align-items-center mb-2"
              >
                {PERSON_FIELDS.map(field => (
                  <input
                    key={field}
                    type="text"
                    className={`form-control form-control-sm mr-2 coverpage-contributor-${field}`}
                    placeholder={PERSON_FIELD_LABELS[field]}
                    aria-label={`${PERSON_FIELD_LABELS[field]} (${group.role} ${index + 1})`}
                    maxLength={200}
                    value={person[field]}
                    onChange={event =>
                      setGroups(
                        updatePerson(groups, group.key, index, field, event.target.value)
                      )
                    }
                  />
                ))}
                <button
                  type="button"
                  className="btn btn-link btn-sm px-1"
                  aria-label="Move up"
                  disabled={index === 0}
                  onClick={() => setGroups(movePerson(groups, group.key, index, -1))}
                >
                  <i className="fa-light fa-arrow-up fa-fw" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="btn btn-link btn-sm px-1"
                  aria-label="Move down"
                  disabled={index === group.people.length - 1}
                  onClick={() => setGroups(movePerson(groups, group.key, index, 1))}
                >
                  <i className="fa-light fa-arrow-down fa-fw" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="btn btn-link btn-sm px-1 text-danger"
                  aria-label="Remove"
                  onClick={() => setGroups(removePerson(groups, group.key, index))}
                >
                  <i className="fa-light fa-xmark fa-fw" aria-hidden="true" />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-link btn-sm pl-0"
              onClick={() => setGroups(addPerson(groups, group.key))}
            >
              <i className="fa-light fa-plus fa-fw mr-1" aria-hidden="true" />
              Add person
            </button>
          </div>
        ))
      )}
      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={onClose}
          className="btn btn-myed btn-default btn-default-myed mr-3"
          disabled={isBusy}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="btn btn-myed btn-primary btn-primary-myed"
          disabled={isBusy || loading}
        >
          Save
        </button>
      </div>
    </MEModal>
  )
}
